import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { Box, HStack, Text, VStack } from "native-base";
import { ProviderConnectionStatus, forHumans, getServiceColour, logisticsForHumans, serviceForHumans, } from "@madmedical/medical";
import { useTranslation } from "@madmedical/i18n";
import { useRouteNavigate } from "../../providers/routing";
import Avatar from "../../atoms/Avatar/Avatar";
import Headline from "../../molecules/Headline";
import Breadcrumb from "../../molecules/Breadcrumb";
import Paper from "../../atoms/Paper";
import SimpleListItem from "../../molecules/ListItem/SimpleListItem";
import Tag from "../../atoms/Tag";
import Badge from "../../atoms/Badge";
import ActionButton from "../../molecules/ActionButton";
import ReactTable from "../../molecules/ReactTable";
import { useCurrentPatient } from "../../providers/currentPatient";
const MAX_TAGS = 3;
const CallCenterPatient = ({ devices, onEditPress }) => {
    const { patient } = useCurrentPatient();
    const navigate = useRouteNavigate();
    const handleRowPress = ({ id }) => {
        navigate("callcenter_device", { deviceId: id });
    };
    const { t } = useTranslation();
    const columns = useMemo(() => [
        {
            Header: t("serialNumber"),
            accessor: "serialNumber",
            width: "10%",
        },
        {
            Header: t("device"),
            id: "deviceTypeName",
            width: "auto",
            Cell: ({ row: { original: { deviceType }, }, }) => _jsx(_Fragment, { children: deviceType.name }),
        },
        {
            Header: t("measures:type"),
            id: "metricTypes",
            width: "12%",
            Cell: ({ row: { original: { deviceType: { metricTypes }, }, }, }) => {
                const count = metricTypes.length;
                if (!count) {
                    return _jsx(Text, { children: "-" });
                }
                const types = metricTypes.slice(0, MAX_TAGS);
                const remains = count - types.length;
                return (_jsxs(_Fragment, { children: [types.map((metricType) => (_jsx(Tag, Object.assign({ mr: 2 }, { children: t(`measures:${forHumans(metricType)}`) }), metricType))), remains ? (_jsxs(Tag, Object.assign({ mr: 2 }, { children: ["+", remains] }), remains)) : null] }));
            },
        },
        {
            Header: t("logistics"),
            accessor: "status",
            width: "12%",
            Cell: ({ value: status }) => (_jsx(Text, { children: logisticsForHumans(status) })),
        },
        {
            Header: t("connectionState"),
            accessor: "connectionStatus",
            width: "12%",
            Cell: ({ value: connectionStatus }) => {
                let badgeData = null;
                switch (connectionStatus) {
                    case ProviderConnectionStatus.Active:
                        badgeData = {
                            variant: "ghostSuccess",
                            text: t("active"),
                        };
                        break;
                    case ProviderConnectionStatus.Inactive:
                        badgeData = {
                            variant: "ghostError",
                            text: t("inactive"),
                        };
                        break;
                    default:
                        badgeData = {
                            variant: "ghost",
                            text: t("notConnected"),
                        };
                }
                return (_jsx(Badge, Object.assign({ mt: 2, size: "sm", variant: badgeData.variant }, { children: badgeData.text })));
            },
        },
        {
            Header: "",
            id: "actions",
            width: 40,
            Cell: ({ row }) => (_jsx(ActionButton, { actions: [
                    {
                        text: t("edit"),
                        onPress: () => onEditPress(row.original.id),
                    },
                ] })),
        },
    ], [onEditPress, t]);
    return (_jsxs(VStack, Object.assign({ space: "3" }, { children: [_jsxs(Paper, { children: [_jsx(Headline, { borderBottomWidth: 1, borderBottomColor: "gray.100", borderBottomStyle: "solid", borderTopRadius: "lg", leftContent: _jsxs(Breadcrumb, { children: [_jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Link, Object.assign({ route: "patients", params: {} }, { children: t("patients") })) }), _jsx(Breadcrumb.Item, { children: _jsx(Breadcrumb.Text, { children: patient.fullName }) })] }) }), _jsxs(HStack, Object.assign({ p: "6", alignItems: "center" }, { children: [_jsx(Avatar, { source: "https://randomuser.me/api/portraits/women/17.jpg", size: "3xl" }), _jsx(Text, Object.assign({ fontSize: "2xl" }, { children: patient.fullName }))] }))] }), _jsxs(Paper, { children: [_jsx(Headline, { title: t("personalData"), size: "xs" }), _jsxs(HStack, Object.assign({ px: "5", width: "full" }, { children: [_jsx(SimpleListItem, { label: t("name"), content: patient.fullName, justifyContent: "flex-start", flex: "1", px: "0" }), _jsx(SimpleListItem, { label: t("packages"), content: _jsx(HStack, Object.assign({ space: 1, flexWrap: "wrap", flex: 1, width: "full" }, { children: patient.services.map((service) => (_jsx(Tag, Object.assign({ variant: getServiceColour(service), mb: 2 }, { children: serviceForHumans(service) }), service))) })), justifyContent: "flex-start", flex: "1", px: "0", borderBottomWidth: "0", width: "full" })] }))] }), _jsxs(Paper, { children: [_jsx(Headline, { title: t("devices"), size: "xs" }), _jsx(Box, Object.assign({ p: "5", width: "full" }, { children: _jsx(ReactTable, { columns: columns, data: devices, onPress: handleRowPress }) }))] })] })));
};
export default CallCenterPatient;
